/*
 * Purpose: Compose Access Shell layout with three panels: left (branding), middle (login/register), right (legal guardian).
 * Boundary: Layout only; no business logic; delegates tab state and forms to parent.
 * Invariants:
 * - Consumes { mode, onTab, renderLogin, renderRegister, services, isMinor, setGuardianState } from props.
 * - ASCII only; no Unicode in comments or strings.
 */
// Imports
import AccessShellLeftPanelLayout from './layouts/accessshell-leftpanel-layout';
import AccessShellMiddlePanelLayout from './layouts/accessshell-middlepanel-layout';
import AccessShellRightPanelLayout from './layouts/accessshell-rightpanel-layout';
import { TabButton, CountryHeader } from './accessshell-controls';
import { useToast } from '../../toast/toast-slim';
import { useEffect, useState, useRef } from 'react';


function useMediaQuery(query) {
    const get = () =>
        typeof window !== 'undefined' ? window.matchMedia(query).matches : false;

    const [matches, setMatches] = useState(get);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mql = window.matchMedia(query);
        const onChange = (e) => setMatches(e.matches);

        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else mql.addListener(onChange);

        setMatches(mql.matches);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else mql.removeListener(onChange);
        };
    }, [query]);

    return matches;
}

// Component AccessShellLayout:
// Full-width Smart-ID style card with header tabs and three panels
export default function AccessShellLayout({
    mode,
    onTab,
    renderLogin,
    renderRegister,
    services,
    isMinor,
    guardianState,
    setGuardianState
}) {
    // Use useToast at the top level to get the toastr object
    const toastr = useToast();

    // Local override: tap-out hides the right panel and re-shows middle
    const [hideGuardian, setHideGuardian] = useState(false);

    // Transition state
    const [showTransitionMessage, setShowTransitionMessage] = useState(false);
    const [transitionMessage, setTransitionMessage] = useState('');
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Track previous isMinor to detect changes
    const prevIsMinorRef = useRef(isMinor);
    const prevGuardianValidRef = useRef(guardianState?.isValid);

    // Tap-out handler: reset guardian state and hide panel (with transition on mobile)
    const handleGuardianTapout = () => {
        const isMobileNow = typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;

        if (isMobileNow) {
            // Show transition message on mobile
            setTransitionMessage('Returning to registration...');
            setShowTransitionMessage(true);
            setIsTransitioning(true);

            // After 2 seconds, hide message and complete tapout
            setTimeout(() => {
                setShowTransitionMessage(false);
                setHideGuardian(true);
                setGuardianState?.({
                    isValid: false,
                    idNumber: '',
                    firstName: '',
                    lastName: '',
                    cellNumber: '',
                    isValidOTP: false,
                    tcAccepted: false,
                    age: null
                });
                setIsTransitioning(false);
            }, 2000);
        } else {
            // Desktop: immediate tapout
            setHideGuardian(true);
            setGuardianState?.({
                isValid: false,
                idNumber: '',
                firstName: '',
                lastName: '',
                cellNumber: '',
                isValidOTP: false,
                tcAccepted: false,
                age: null
            });
        }
    };

    // Reset the tap-out override whenever minor status or the entered ID changes
    useEffect(() => {
        setHideGuardian(false);
    }, [isMinor, guardianState?.idNumber]);

    // Mobile detection
    const isMobile = useMediaQuery?.('(max-width: 767.98px)') ?? (
        typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches
    );

    // Effect: Detect when minor changes from false to true (Scenario A)
    useEffect(() => {
        const wasNotMinor = prevIsMinorRef.current === false;
        const isNowMinor = isMinor === true;

        if (wasNotMinor && isNowMinor && isMobile && !isTransitioning) {
            // Minor just detected on mobile - show transition message
            setTransitionMessage('You are a minor. Please provide Legal Guardian information first.');
            setShowTransitionMessage(true);
            setIsTransitioning(true);

            // After 2 seconds, hide message
            setTimeout(() => {
                setShowTransitionMessage(false);
                setIsTransitioning(false);
            }, 2000);
        }

        prevIsMinorRef.current = isMinor;
    }, [isMinor, isMobile, isTransitioning]);

    // Effect: Auto tapout when guardian becomes valid (Scenario B)
    useEffect(() => {
        const wasNotValid = prevGuardianValidRef.current !== true;
        const isNowValid = guardianState?.isValid === true;

        if (wasNotValid && isNowValid && isMobile && isMinor && !hideGuardian && !isTransitioning) {
            // Guardian just became valid on mobile - auto tapout
            setTransitionMessage('Legal Guardian validated! Returning to registration...');
            setShowTransitionMessage(true);
            setIsTransitioning(true);

            // After 2 seconds, hide message and complete tapout
            setTimeout(() => {
                setShowTransitionMessage(false);
                setHideGuardian(true);
                setIsTransitioning(false);
            }, 4000);
        }

        prevGuardianValidRef.current = guardianState?.isValid;
    }, [guardianState?.isValid, isMobile, isMinor, hideGuardian, isTransitioning]);

    // 3) Right: show whenever minor — UNLESS user tapped out
    const showRightPanel = isMinor === true && !hideGuardian;

    // 1) Left: hide on mobile; hide on desktop when guardian active
    const showLeftPanel = !isMobile && !showRightPanel;

    // 2) Middle: show by default; HIDE only when it's mobile AND minor AND guardian not valid,
    //    OR force-show if user tapped out
    const showMiddlePanel = hideGuardian || !(isMobile && isMinor && guardianState?.isValid !== true);

    // Rendering - build shell with header and panels
    return (
        <main className="h-screen w-screen p-4 flex flex-col">
            <section className="w-full flex-1 flex flex-col">
                <div
                    className="relative overflow-hidden rounded-[22px] bg-neutral-900 shadow-[0_12px_40px_rgb(0_0_0_/0.45)] ring-1 ring-neutral-800 flex flex-col h-full"
                    aria-label="Smart ID card"
                >
                    {/* Surface layers - subtle highlights and header strip */}
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute inset-0 bg-[radial-gradient(120%_120%_at_0%_0%,rgba(255,255,255,0.06),transparent_55%)]" />
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),transparent_22%,transparent_78%,rgba(0,0,0,0.35))]" />
                    </div>
                    <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 opacity-80" />

                    {/* Transition Message Overlay */}
                    {showTransitionMessage && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center bg-neutral-900/95 backdrop-blur-sm animate-fadeIn">
                            <div className="max-w-md mx-4 p-6 bg-neutral-800 rounded-xl ring-1 ring-emerald-600 shadow-2xl">
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                        <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm md:text-base text-neutral-200 leading-relaxed">
                                            {transitionMessage}
                                        </p>
                                    </div>
                                </div>
                                {/* Progress indicator */}
                                <div className="mt-4 h-1 bg-neutral-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-emerald-500 rounded-full animate-progress"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Header - SA flag, title, tabs, and toast test buttons */}
                    <div className="relative flex flex-wrap items-center justify-between gap-2 px-3 py-2 md:px-5 md:py-3 bg-neutral-800/60">
                        {/* Left side - Country Header */}
                        <CountryHeader />

                        {/* DEBUG: panel flags
                        <div className="mt-3 space-y-2 text-xs font-mono text-blue-200">
                            <label className="block">
                                <div>showLeftPanel</div>
                                <div>{String(showLeftPanel)}</div>
                                <div>logic: !isMobile</div>
                                <div>{"isMobile"}:{String(isMobile)}</div>
                            </label>
                            <label className="block">
                                <div>showMiddlePanel</div>
                                <div>{String(showMiddlePanel)}</div>
                                <div>logic: hideGuardian || !(isMobile && isMinor && !guardianValid)</div>
                                <div>{"hideGuardian"}:{String(hideGuardian)} {"isMobile"}:{String(isMobile)} {"isMinor"}:{String(isMinor)} {"guardianValid"}:{String(guardianState?.isValid)}</div>
                            </label>
                            <label className="block">
                                <div>showRightPanel</div>
                                <div>{String(showRightPanel)}</div>
                                <div>logic: isMinor && !hideGuardian</div>
                                <div>{"isMinor"}:{String(isMinor)} {"hideGuardian"}:{String(hideGuardian)}</div>
                            </label>
                        </div>
                         */}

                        {/* Right side - Tabs and Debug Buttons */}
                        <div className="flex items-center gap-3">
                            {/* Tabs - login/register toggle */}
                            <div className="flex rounded-lg bg-neutral-900 p-1 ring-1 ring-neutral-800">
                                <TabButton
                                    id="tab-login"
                                    selected={mode === 'login'}
                                    controls="panel-login"
                                    onClick={onTab('login')}
                                >
                                    Login
                                </TabButton>
                                <TabButton
                                    id="tab-register"
                                    selected={mode === 'register'}
                                    controls="panel-register"
                                    onClick={onTab('register')}
                                >
                                    Register
                                </TabButton>
                            </div>
                        </div>
                        {/* Mobile: Full title below header row */}
                        <div className="w-full md:hidden text-sm font-semibold leading-tight">
                            OATHS - Your Digital Commissioner of Oaths
                        </div>
                    </div>

                    {/* Content */}
                    <div
                        className="relative grid grid-cols-1 md:grid-cols-2 gap-6 p-4 md:p-8 flex-1"
                    >
                        {/* Left panel: hide/show without unmounting */}
                        <div className={showLeftPanel ? 'block md:col-span-1' : 'hidden'} aria-hidden={!showLeftPanel}>
                            <AccessShellLeftPanelLayout />
                        </div>

                        {/* Middle panel: ALWAYS MOUNTED; just hide on mobile+minor+guardian !valid */}
                        <div
                            className={showMiddlePanel ? 'block' : 'hidden'}
                            aria-hidden={!showMiddlePanel}
                        >
                            <AccessShellMiddlePanelLayout
                                mode={mode}
                                renderLogin={renderLogin}
                                renderRegister={renderRegister}
                                showRightPanel={showRightPanel}
                            />
                        </div>

                        {/* Right panel: hide/show without unmounting */}
                        <div className={showRightPanel ? 'block md:col-span-1' : 'hidden'} aria-hidden={!showRightPanel}>
                            <AccessShellRightPanelLayout
                                services={services}
                                setGuardianState={setGuardianState}
                                onTapout={handleGuardianTapout}
                                isMobile={isMobile}
                            />
                        </div>
                    </div>

                    {/* Footer strip - Oaths attribution (static white text, three lines) */}
                    <div className="px-4 pb-3 space-y-0.5">
                        <p className="text-[10px] leading-4 text-white text-center select-none">
                            &#169;&nbsp;Oaths.co.za&nbsp;&trade; - Your Digital Commissioner of Oaths
                        </p>
                        <p className="text-[10px] leading-4 text-white text-center select-none">
                            All certifications are performed by duly appointed Commissioners of Oaths in accordance with South African law.
                        </p>
                        <p className="text-[10px] leading-4 text-white text-center select-none">
                            Built for South Africans, locally and abroad.
                        </p>
                    </div>
                    {/* Outline ring */}
                    <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-white/5" />
                </div>
                {/* Footer note removed -- replaced by in-card Oaths attribution above */}
            </section>

            {/* CSS Animations */}
            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
                
                @keyframes progress {
                    from {
                        width: 0%;
                    }
                    to {
                        width: 100%;
                    }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-in-out;
                }
                
                .animate-progress {
                    animation: progress 2s ease-in-out forwards;
                }
            `}</style>
        </main>
    );
}